import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import DataTable, { Column } from '@repo/ui/data-table'
import { ExclamationMarkIcon, UserFiveIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ReactNode, useMemo } from 'react'

import { getAffectedStaffs } from '../../../api/affected-staffs/get-affected-staffs'
import { getCenterRequestedStaffSlots } from '../../../api/affected-staffs/get-center-requested-staff-slots'
import { getCenter } from '../../../api/centers/get-center'
import { getCenterManager } from '../../../api/centers/get-center-manager'
import { getOlPermissions } from '../../../lib/constants/ol-permissions'
import { Paths } from '../../../lib/constants/path'
import { useObserver } from '../../../lib/contexts/observer'
import useAuth from '../../../lib/hooks/use-auth'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { AccomodationEnum } from '../../../lib/types/enums/accomodation-type/accomodation-type'
import { AffectedStaffStatusEnum } from '../../../lib/types/enums/affected-staff/affected-staff-status.enum'
import { genderOneLetterMapping } from '../../../lib/types/enums/common/gender.enum'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import {
  affectedStaffOlCenterTableFields,
  AffectedStaffOlCenterTableType
} from '../../../lib/types/fields/affected-staffs/affected-staff'
import { AffectedStaff } from '../../../lib/types/models/affectedStaff/affected-staff'
import { AffectedStaffFilters } from '../../../lib/types/models/affectedStaff/affected-staff-input'
import { HostFamilyProperties } from '../../../lib/types/models/host-family/properties.host-family'
import InformationToolTip from '../../elements/information-tooltip'
import { QualificationBadge } from '../../elements/qualification-badge'
import StaffProfileCard from '../../elements/staff-profile-card'
import OlCenterHostFamilyPlacementHistory from './ol-center-placement-enrollment-table/ol-center-host-family-placement-history'

type Props = {
  actions?: ReactNode[]
  filter?: AffectedStaffFilters
  centerId?: number
  accomodation: AccomodationEnum
  extraColumns?: Column<AffectedStaff | AffectedStaffOlCenterTableType>[]
  mode?: 'french-view' | 'foriegn-view'
}

const OlCenterAffectedStaffTable = ({
  actions,
  filter,
  centerId,
  accomodation,
  mode,
  extraColumns = []
}: Props) => {
  const { navigate } = useNavigator()
  const { t } = useTranslate()
  const { userRole } = useAuth()
  const queryClient = useQueryClient()
  const initialFilter: AffectedStaffFilters = {
    centerId,
    statuses: [AffectedStaffStatusEnum.AFFECTED],
    ...filter
  }
  const affectedStaffsQueryKey = [
    'affected-staffs',
    'ol-center-table',
    centerId,
    'FULL',
    initialFilter
  ]
  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'view-affected-staff',
    Pick<AffectedStaffOlCenterTableType, 'staff' | 'staffRequest'>
  >(['view-affected-staff'])

  const { data: center } = useQuery({
    queryKey: ['centers', centerId, 'COMPACT'],
    queryFn: () => {
      if (!centerId) return
      return getCenter(centerId, 'COMPACT')
    },
    enabled: !!centerId
  })

  const { data: centerManager } = useQuery({
    queryKey: ['center-manager', centerId, affectedStaffOlCenterTableFields, mode],
    queryFn: () => {
      if (!centerId) return
      return getCenterManager(centerId, affectedStaffOlCenterTableFields)
    },
    enabled: !!centerId
  })

  const { data: affectedStaffs, isFetching } = useQuery({
    queryKey: affectedStaffsQueryKey,
    queryFn: () => getAffectedStaffs({ filter: initialFilter }, 'FULL'),
    enabled: !!centerId
  })

  const requestedStaffSlotsQueryKey = ['affected-staffs', 'requested-staff-slots', centerId]
  const { data: requestedStaffSlots } = useQuery({
    queryKey: requestedStaffSlotsQueryKey,
    queryFn: () => {
      if (!centerId) return
      return getCenterRequestedStaffSlots(centerId)
    },
    enabled: !!centerId
  })

  useObserver({
    key: 'ol-affected-staff-table',
    update: () => {
      if (!centerId) return
      queryClient.invalidateQueries({ queryKey: affectedStaffsQueryKey })
      queryClient.invalidateQueries({ queryKey: requestedStaffSlotsQueryKey })
      queryClient.invalidateQueries({ queryKey: ['center-manager', centerId] })
    }
  })

  const olPermissions = useMemo(
    () => getOlPermissions({ role: userRole, type: center?.ol?.type }),
    [center, userRole]
  )

  const columns: Column<AffectedStaff | AffectedStaffOlCenterTableType>[] = [
    {
      enableSorting: true,
      accessorKey: 'staff.lastName',
      columnHeaderClassName: 'w-[150px]',
      header: t('affected.staffs.table.lastName'),
      cell: ({ row }) => {
        if (!row.original.staff) return '-'
        const lastNameLink = (
          <Typography.Link
            variant="label-semibold"
            color={tailwindColors.blue.DEFAULT}
            onClick={(e) => {
              e.stopPropagation()
              if (userRole === RoleEnum.Ol) toggleModal('view-affected-staff', row.original)
              else
                navigate(
                  Paths.AFFECTED_STAFFS_VIEW_PAGE.replace(':id', row.original.id.toString())
                )
            }}
          >
            {row.original.staff.lastName.toUpperCase()}
          </Typography.Link>
        )
        return row.original.status === AffectedStaffStatusEnum.PENDING ? (
          <InformationToolTip icon={<Badge text={lastNameLink} variant={'blue'} />} size="small">
            <Typography variant={'small-semibold'} color={tailwindColors.white.DEFAULT}>
              Non confirmé
            </Typography>
          </InformationToolTip>
        ) : (
          lastNameLink
        )
      }
    },
    {
      enableSorting: true,
      accessorKey: 'staff.firstName',
      columnHeaderClassName: 'w-[150px]',
      header: t('affected.staffs.table.firstName'),
      cell: ({ row }) =>
        row.original.staff ? StringFormatter.capitalize(row.original.staff.firstName) : '-'
    },
    {
      accessorKey: 'staffRequest.qualification',
      header: t('affected.staffs.table.qualification'),
      columnHeaderClassName: 'w-[200px]',
      cell: ({ row }) => (
        <QualificationBadge qualification={row.original.staffRequest.qualification} olLabel />
      )
    },
    ...(mode === 'french-view'
      ? [
          {
            enableSorting: true,
            accessorKey: 'staff.birthDate',
            header: t('affected.staffs.table.age'),
            columnHeaderClassName: 'w-[80px]',
            cell: ({
              row
            }: {
              row: { original: AffectedStaff | AffectedStaffOlCenterTableType }
            }) =>
              row.original.staff?.birthDate
                ? DateFormatter.calculateAge(row.original.staff.birthDate)
                : '-'
          },
          {
            enableSorting: true,
            accessorKey: 'staff.gender',
            header: t('affected.staffs.table.gender'),
            cell: ({
              row
            }: {
              row: { original: AffectedStaff | AffectedStaffOlCenterTableType }
            }) =>
              row.original.staff
                ? valueRender(genderOneLetterMapping[row.original.staff.gender]?.label)
                : '-'
          },
          {
            accessorKey: 'hostFamily.parent1FirstName',
            hidden: accomodation !== AccomodationEnum.HOST_FAMILY || !olPermissions['host-family'],
            header: t('affected.staffs.table.accommodation'),
            cell: ({
              row
            }: {
              row: { original: AffectedStaff | AffectedStaffOlCenterTableType }
            }) => {
              if (!row.original.hostFamily) return '-'
              return (
                <div className="flex gap-2 items-center">
                  <Typography.Link
                    variant="label-semibold"
                    onClick={(e) => {
                      if (!row.original.hostFamily) return
                      e.stopPropagation()
                      if (userRole === RoleEnum.ADMIN)
                        navigate(
                          Paths.OL_HOST_FAMILY_INFORMATION.replace(
                            ':id',
                            row.original.hostFamily.id.toString()
                          )
                        )
                      else
                        navigate(
                          Paths.OL_PERSONAL_SPACE_HOST_FAMILIES_UPDATE_PAGE.replace(
                            ':id',
                            row.original.hostFamily.id.toString()
                          )
                        )
                    }}
                  >
                    {toFullName(
                      row.original.hostFamily?.parent1FirstName,
                      row.original.hostFamily?.parent1LastName,
                      { options: { mode: 'lastname-firstname' } }
                    )}
                  </Typography.Link>
                  {HostFamilyProperties.hasVisibleLecComment(
                    row.original.hostFamily,
                    userRole
                  ) && (
                    <div className="flex items-center justify-center w-6 h-6 bg-red-light rounded-full">
                      <div className="flex items-center justify-center h-4 w-4 rounded-full bg-red">
                        <ExclamationMarkIcon filled color={tailwindColors.white.DEFAULT} />
                      </div>
                    </div>
                  )}
                  {(row.original?.hostFamilyHistory?.length ?? 0) > 0 && (
                    <OlCenterHostFamilyPlacementHistory
                      hostFamilyHistory={row.original?.hostFamilyHistory}
                    />
                  )}
                </div>
              )
            }
          }
        ]
      : [])
  ]
  if (isFetching) return <Loader center />

  const affectedRows = centerManager
    ? [
        centerManager,
        ...(affectedStaffs?.data?.filter((staff) => staff.id !== centerManager.id) || [])
      ]
    : affectedStaffs?.data || []

  // For every requested staff slot that is not yet filled by an AFFECTED staff,
  // render a placeholder row. Only the qualification is known at this point, so
  // every other attribute is shown as "-" (handled by the column cells above).
  const placeholderRows = (requestedStaffSlots ?? [])
    .flatMap((slot) => Array.from({ length: slot.unfilledCount }, () => slot.qualification))
    .map((qualification, index) => ({
      id: -(index + 1),
      staffRequest: { qualification }
    })) as unknown as AffectedStaffOlCenterTableType[]

  const data = [...affectedRows, ...placeholderRows]

  return (
    <>
      {actions &&
        actions.length > 0 &&
        actions.map((action, index) => (
          <div key={index} className="mb-2">
            {action}
          </div>
        ))}
      <DataTable<AffectedStaffOlCenterTableType>
        bodyVariant="white"
        data={data}
        manualSorting={false}
        rowCount={data.length}
        paginated={false}
        columns={[...columns, ...extraColumns]}
      />

      {selectedData?.staff && (
        <Modal
          isOpen={modalState['view-affected-staff']}
          icon={UserFiveIcon}
          size="medium"
          title={toFullName(
            selectedData.staff.lastName.toUpperCase(),
            selectedData.staff.firstName
          )}
          actionButtonsOptions={{ cancelText: 'Fermer' }}
          onCancel={() => closeModel('view-affected-staff')}
        >
          <StaffProfileCard
            className={MODAL_CONTENT_CLASSNAME}
            staff={selectedData.staff}
            qualification={selectedData.staffRequest.qualification}
          />
        </Modal>
      )}
    </>
  )
}

export default OlCenterAffectedStaffTable
